/**
 * ButterUI — Theme Utilities
 *
 * Runtime helpers for applying color themes via CSS custom properties.
 * Works in any browser environment (Capacitor WebView, regular browser).
 *
 * Usage:
 *   import { applyTheme, isValidThemeName } from '@butter/ui/styles/themeUtils';
 *   applyTheme('green');                        // sets --theme-* vars on <html>
 *   applyTheme('green', someElement);           // sets on a specific element
 *   isValidThemeName(userInput)                 // type guard
 *
 * @see packages/ui/docs/ColorTheming.mdx for architecture details
 */

import { themes, DEFAULT_THEME, THEME_SHADES } from './themes';
import type { ThemeName, ThemeShade } from './themes';

// ── CSS variable helpers ────────────────────────────────────────────

/**
 * CSS custom property name for a given shade.
 *
 * @example getThemeCSSVar(500) → '--theme-500'
 */
export function getThemeCSSVar(shade: ThemeShade): string {
  return `--theme-${shade}`;
}

// ── Apply theme ─────────────────────────────────────────────────────

/**
 * Apply a theme by setting CSS custom properties on an element.
 * Typically called on `document.documentElement` (the `<html>` element).
 *
 * If `themeName` is not found in the registry, falls back to the default theme.
 *
 * @param themeName - A registered theme name (e.g. 'purple', 'green')
 * @param element   - Target DOM element (defaults to `document.documentElement`)
 */
export function applyTheme(
  themeName: ThemeName,
  element: HTMLElement = document.documentElement,
): void {
  const palette = themes[themeName] ?? themes[DEFAULT_THEME];

  for (const shade of THEME_SHADES) {
    element.style.setProperty(getThemeCSSVar(shade), palette[shade]);
  }
}

// ── Query helpers ───────────────────────────────────────────────────

/**
 * Get the full set of CSS custom property declarations for the default theme.
 * Useful for generating the `:root` block in CSS or for SSR.
 *
 * @returns Record of `--theme-{shade}` → hex value
 */
export function getDefaultCSSVars(): Record<string, string> {
  const palette = themes[DEFAULT_THEME];
  const vars: Record<string, string> = {};

  for (const shade of THEME_SHADES) {
    vars[getThemeCSSVar(shade)] = palette[shade];
  }

  return vars;
}

/**
 * Type-guard: validate that a string is a registered theme name.
 *
 * Own properties only. `name in themes` would walk the prototype chain, so
 * `'constructor'`, `'toString'` and friends would narrow to `ThemeName`;
 * `applyTheme('toString')` then resolves a *function*, which is truthy enough to
 * skip the `?? themes[DEFAULT_THEME]` fallback and writes `undefined` for every
 * shade. This guard sits directly downstream of the persisted theme cache, which
 * is user-writable, so it validates untrusted input.
 *
 * @example
 *   const raw: string = tenant.colorTheme;
 *   if (isValidThemeName(raw)) {
 *     applyTheme(raw); // raw is narrowed to ThemeName
 *   }
 */
export function isValidThemeName(name: string): name is ThemeName {
  return Object.prototype.hasOwnProperty.call(themes, name);
}

// Re-export types so consumers can import from one place
export type { ThemeName, ThemeShade, ThemePalette } from './themes';
